<script setup lang="ts">
import type { OnboardingStepNextHandler } from './types'

import { all } from '@proj-airi/i18n'
import { AnimatedContent } from '@proj-airi/ui'
import { storeToRefs } from 'pinia'
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuRoot,
  DropdownMenuTrigger,
} from 'reka-ui'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import onboardingLogo from '../../../../assets/onboarding.avif'

import { useSettingsGeneral } from '../../../../stores/settings'

interface Props {
  customProviderSetupEnabled: boolean
  onNext: OnboardingStepNextHandler
  /**
   * Completes onboarding without asking for anything. Aura ships configured: the Lumina
   * proxy holds provider credentials server-side, so there is no key for the user to paste.
   * Supplied by onboarding.vue; falls back to the ordinary step advance when absent, which
   * is what the component stories and the desktop build do.
   */
  onEnter?: () => Promise<void> | void
}

const props = defineProps<Props>()
const { t } = useI18n()
const settingsStore = useSettingsGeneral()
const { language } = storeToRefs(settingsStore)

const entering = ref(false)

const languages = computed(() => {
  return Object.entries(all).map(([value, label]) => ({ value, label }))
})

async function handleEnter() {
  if (entering.value)
    return

  entering.value = true
  try {
    await (props.onEnter ? props.onEnter() : props.onNext())
  }
  finally {
    entering.value = false
  }
}
</script>

<template>
  <div relative h-full flex flex-col>
    <div :class="['absolute', 'right-0', 'top-0', 'z-10']">
      <DropdownMenuRoot>
        <DropdownMenuTrigger
          :class="[
            'h-8 w-8',
            'flex items-center justify-center',
            'rounded-lg',
            'outline-none',
            // Fixed dark-surface colours rather than theme-following neutrals: this
            // control sits on the glass panel, which is dark in both schemes.
            'lumina-muted transition-colors duration-200',
            'hover:bg-white/10 hover:text-white',
            'data-[state=open]:bg-white/10',
          ]"
          :aria-label="t('settings.language.title')"
        >
          <div class="i-lucide:globe" h-5 w-5 />
        </DropdownMenuTrigger>
        <DropdownMenuPortal>
          <DropdownMenuContent
            as-child
            align="end"
            side="bottom"
            :side-offset="6"
            :class="[
              'lumina-glass',
              'z-10000 min-w-36 p-1 outline-none',
            ]"
          >
            <AnimatedContent>
              <DropdownMenuItem
                v-for="lang in languages"
                :key="lang.value"
                :class="[
                  'flex cursor-pointer select-none items-center rounded-lg px-3 py-2',
                  'text-sm leading-none outline-none',
                  'data-[highlighted]:bg-white/10',
                  'transition-colors duration-150 ease-in-out',
                  lang.value === language ? 'lumina-wordmark' : '',
                ]"
                @select="() => language = lang.value"
              >
                {{ lang.label }}
              </DropdownMenuItem>
            </AnimatedContent>
          </DropdownMenuContent>
        </DropdownMenuPortal>
      </DropdownMenuRoot>
    </div>
    <div :class="['mb-2', 'flex', 'flex-1', 'flex-col', 'justify-center', 'text-center', 'md:mb-8']">
      <div
        v-motion
        :initial="{ opacity: 0, scale: 0.5 }"
        :enter="{ opacity: 1, scale: 1 }"
        :duration="500"
        :class="['mb-1', 'flex', 'justify-center', 'md:mb-4', 'md:pt-8', 'lg:pt-16']"
      >
        <div class="lumina-avatar-ring">
          <img :src="onboardingLogo" max-h="50" aspect-square h-auto w-auto select-none object-cover>
        </div>
      </div>
      <h2
        v-motion
        :initial="{ opacity: 0, y: 10 }"
        :enter="{ opacity: 1, y: 0 }"
        :duration="500"
        :class="['lumina-wordmark', 'mb-0', 'text-3xl', 'font-bold', 'md:mb-2']"
      >
        {{ t('settings.dialogs.onboarding.title') }}
      </h2>
      <p
        v-motion
        :initial="{ opacity: 0, y: 10 }"
        :enter="{ opacity: 1, y: 0 }"
        :duration="500"
        :delay="100"
        :class="['lumina-muted', 'text-sm', 'md:text-lg']"
      >
        {{ t('settings.dialogs.onboarding.description') }}
      </p>
    </div>
    <div :class="['flex', 'm-2']">
      <button
        v-motion="{
          initial: { opacity: 0 },
          enter: { opacity: 1 },
          duration: 500,
          delay: 200,
        }"
        class="lumina-enter-button"
        type="button"
        :disabled="entering"
        @click="handleEnter"
      >
        {{ t('settings.dialogs.onboarding.enter') }}
      </button>
    </div>
  </div>
</template>
